import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from './Modal';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordModal({ open, onClose }: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('changePassword.tooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('changePassword.mismatch'));
      return;
    }

    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) {
      setError(err.message);
    } else {
      setSuccess(true);
    }
  };

  const handleClose = () => {
    setPassword('');
    setConfirm('');
    setSuccess(false);
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('changePassword.title')}>
      {success ? (
        <div className="text-center py-4">
          <p className="text-sm font-semibold text-emerald-700">{t('changePassword.success')}</p>
          <button
            onClick={handleClose}
            className="mt-6 w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm hover:bg-stone-800 transition-colors"
          >
            OK
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={t('changePassword.newPassword')}
            className="w-full px-5 py-4 bg-stone-50 rounded-2xl border border-stone-200 text-sm font-semibold text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
          />
          <input
            type="password"
            required
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder={t('changePassword.confirmPassword')}
            className="w-full px-5 py-4 bg-stone-50 rounded-2xl border border-stone-200 text-sm font-semibold text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
          />
          {error && <p className="text-sm font-semibold text-rose-500">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password || !confirm}
            className="w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm hover:bg-stone-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('changePassword.submit')}
          </button>
        </form>
      )}
    </Modal>
  );
}
